import { useEffect, useRef } from 'react'
import { type BarcodeFormat } from '@zxing/library'

import type { DecodeRequest, DecodeResponse } from './barcode-decoder.worker'
import { type DetectedBarcode } from './barcode-scanner.utils'
import { scannerLog as log, scannerWarn as warn } from './scanner-log'

type ZxingBarcodeScannerProps = {
  formats?: readonly BarcodeFormat[]
  constraints?: MediaTrackConstraints
  scanDelay?: number
  mirror?: boolean
  onScan: (detectedCodes: DetectedBarcode[]) => void
  onError: (error: unknown) => void
}

type FocusModeCapability = MediaTrackCapabilities & {
  focusMode?: string[]
}

type FocusModeConstraint = MediaTrackConstraintSet & {
  focusMode?: string
}

type RvfcHandle = number
type RvfcCallback = (now: number, metadata: { mediaTime: number }) => void

type VideoElementWithRvfc = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: RvfcCallback) => RvfcHandle
  cancelVideoFrameCallback?: (handle: RvfcHandle) => void
}

const VIDEO_READY_TIMEOUT_MS = 5000
const TRACKER_CLEAR_DELAY_MS = 900
const SUCCESS_SOUND_THROTTLE_MS = 900
const DECODE_CROP_SCALES = [1, 0.6] as const
const DECODE_INVERT_AT_CROP_SCALE = 0.6

const CAPTURE_MAX_WIDTH = 1920
const CAPTURE_MAX_HEIGHT = 1080
const CAPTURE_TARGET_FRAME_RATE = 30
const CAPTURE_MAX_FRAME_RATE = 30
const DECODE_LONG_EDGE_PX = 1920

const DECODE_LOG_INTERVAL = 30

const buildCappedConstraints = (
  overrides: MediaTrackConstraints | undefined,
): MediaTrackConstraints => {
  return {
    facingMode: { ideal: 'environment' },
    width: { ideal: CAPTURE_MAX_WIDTH, max: CAPTURE_MAX_WIDTH },
    height: { ideal: CAPTURE_MAX_HEIGHT, max: CAPTURE_MAX_HEIGHT },
    frameRate: { ideal: CAPTURE_TARGET_FRAME_RATE, max: CAPTURE_MAX_FRAME_RATE },
    ...overrides,
  }
}

const DEFAULT_SCANNER_CONSTRAINTS: MediaTrackConstraints = buildCappedConstraints(undefined)

const stopMediaStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach((track) => track.stop())
}

const applyBestEffortCaptureCaps = async (stream: MediaStream) => {
  const [videoTrack] = stream.getVideoTracks()

  if (!videoTrack?.applyConstraints) {
    return
  }

  await videoTrack
    .applyConstraints({
      width: { ideal: CAPTURE_MAX_WIDTH, max: CAPTURE_MAX_WIDTH },
      height: { ideal: CAPTURE_MAX_HEIGHT, max: CAPTURE_MAX_HEIGHT },
      frameRate: { ideal: CAPTURE_TARGET_FRAME_RATE, max: CAPTURE_MAX_FRAME_RATE },
    })
    .catch((err) => warn('applyBestEffortCaptureCaps failed:', err))
}

const applyBestEffortAutoFocus = async (stream: MediaStream) => {
  const [videoTrack] = stream.getVideoTracks()

  if (!videoTrack?.getCapabilities || !videoTrack.applyConstraints) {
    log('autofocus: getCapabilities/applyConstraints unavailable on this track')
    return
  }

  const capabilities = videoTrack.getCapabilities() as FocusModeCapability

  if (!capabilities.focusMode?.includes('continuous')) {
    log('autofocus: continuous focus not supported; available modes =', capabilities.focusMode)
    return
  }

  await videoTrack
    .applyConstraints({
      advanced: [{ focusMode: 'continuous' } as FocusModeConstraint],
    })
    .catch((err) => warn('applyBestEffortAutoFocus failed:', err))
}

const playSuccessSound = () => {
  const audioWindow = window as Window & { webkitAudioContext?: typeof AudioContext }
  const AudioContextConstructor = window.AudioContext || audioWindow.webkitAudioContext

  if (!AudioContextConstructor) {
    return
  }

  const audioContext = new AudioContextConstructor()
  const oscillator = audioContext.createOscillator()
  const gain = audioContext.createGain()

  oscillator.type = 'sine'
  oscillator.frequency.value = 880
  gain.gain.setValueAtTime(0.0001, audioContext.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.12)

  oscillator.connect(gain)
  gain.connect(audioContext.destination)
  oscillator.start()
  oscillator.stop(audioContext.currentTime + 0.13)
  oscillator.addEventListener('ended', () => {
    audioContext.close().catch(() => undefined)
  })
}

const extractFacingModeValue = (
  facingMode: ConstrainDOMString | undefined,
): VideoFacingModeEnum | undefined => {
  if (typeof facingMode === 'string') {
    return facingMode as VideoFacingModeEnum
  }
  if (Array.isArray(facingMode)) {
    return facingMode[0] as VideoFacingModeEnum | undefined
  }
  if (facingMode && typeof facingMode === 'object') {
    const constrained = facingMode as ConstrainDOMStringParameters
    const exactValue = Array.isArray(constrained.exact) ? constrained.exact[0] : constrained.exact
    const idealValue = Array.isArray(constrained.ideal) ? constrained.ideal[0] : constrained.ideal
    return (exactValue ?? idealValue) as VideoFacingModeEnum | undefined
  }
  return undefined
}

const getCameraStream = async (constraints: MediaTrackConstraints): Promise<MediaStream> => {
  const cappedConstraints = buildCappedConstraints(constraints)
  let stream: MediaStream

  log('getUserMedia (1st attempt) constraints =', cappedConstraints)
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: cappedConstraints,
      audio: false,
    })
    log('getUserMedia (1st attempt) succeeded')
  } catch (firstErr) {
    warn('getUserMedia (1st attempt) failed:', firstErr)
    const requestedFacingMode = extractFacingModeValue(constraints.facingMode)
    const fallbackConstraints = {
      ...(requestedFacingMode ? { facingMode: { ideal: requestedFacingMode } } : {}),
      width: { ideal: CAPTURE_MAX_WIDTH, max: CAPTURE_MAX_WIDTH },
      height: { ideal: CAPTURE_MAX_HEIGHT, max: CAPTURE_MAX_HEIGHT },
    }
    log('getUserMedia (retry) constraints =', fallbackConstraints)
    stream = await navigator.mediaDevices.getUserMedia({
      video: fallbackConstraints,
      audio: false,
    })
    log('getUserMedia (retry) succeeded')
  }

  await applyBestEffortCaptureCaps(stream)
  await applyBestEffortAutoFocus(stream)

  return stream
}

const waitForVideoReady = (videoElement: HTMLVideoElement): Promise<void> => {
  if (
    videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
    videoElement.videoWidth > 0
  ) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup()
      reject(new Error('Camera preview did not start. Please try another camera or browser.'))
    }, VIDEO_READY_TIMEOUT_MS)

    const cleanup = () => {
      window.clearTimeout(timeoutId)
      videoElement.removeEventListener('loadedmetadata', handleReady)
      videoElement.removeEventListener('canplay', handleReady)
      videoElement.removeEventListener('playing', handleReady)
      videoElement.removeEventListener('resize', handleReady)
    }

    const handleReady = () => {
      if (
        videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
        videoElement.videoWidth <= 0
      ) {
        return
      }

      cleanup()
      resolve()
    }

    videoElement.addEventListener('loadedmetadata', handleReady)
    videoElement.addEventListener('canplay', handleReady)
    videoElement.addEventListener('playing', handleReady)
    videoElement.addEventListener('resize', handleReady)
  })
}

const prepareCanvas = (
  canvasElement: HTMLCanvasElement,
): { ctx: CanvasRenderingContext2D; width: number; height: number } | null => {
  const rect = canvasElement.getBoundingClientRect()
  const width = Math.max(rect.width, 1)
  const height = Math.max(rect.height, 1)
  const pixelRatio = window.devicePixelRatio || 1
  const nextWidth = Math.round(width * pixelRatio)
  const nextHeight = Math.round(height * pixelRatio)

  if (canvasElement.width !== nextWidth || canvasElement.height !== nextHeight) {
    canvasElement.width = nextWidth
    canvasElement.height = nextHeight
  }

  const ctx = canvasElement.getContext('2d')

  if (!ctx) {
    return null
  }

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  ctx.clearRect(0, 0, width, height)

  return { ctx, width, height }
}

const mapVideoPointToCanvas = (
  point: { x: number; y: number },
  videoElement: HTMLVideoElement,
  canvasWidth: number,
  canvasHeight: number,
) => {
  const videoWidth = videoElement.videoWidth || canvasWidth
  const videoHeight = videoElement.videoHeight || canvasHeight
  const scale = Math.max(canvasWidth / videoWidth, canvasHeight / videoHeight)
  const offsetX = (canvasWidth - videoWidth * scale) / 2
  const offsetY = (canvasHeight - videoHeight * scale) / 2

  return {
    x: offsetX + point.x * scale,
    y: offsetY + point.y * scale,
  }
}

const drawResultTracker = (
  canvasElement: HTMLCanvasElement | null,
  videoElement: HTMLVideoElement,
  points: { x: number; y: number }[],
) => {
  if (!canvasElement) {
    return
  }

  const preparedCanvas = prepareCanvas(canvasElement)

  if (!preparedCanvas) {
    return
  }

  const { ctx, width, height } = preparedCanvas
  const mappedPoints = points.map((point) =>
    mapVideoPointToCanvas(point, videoElement, width, height),
  )

  if (!mappedPoints.length) {
    return
  }

  const xValues = mappedPoints.map((point) => point.x)
  const yValues = mappedPoints.map((point) => point.y)
  const minX = Math.min(...xValues)
  const maxX = Math.max(...xValues)
  const minY = Math.min(...yValues)
  const maxY = Math.max(...yValues)
  const padding = 18

  ctx.save()
  ctx.strokeStyle = '#72f5c8'
  ctx.fillStyle = '#72f5c8'
  ctx.lineWidth = 4
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  if (mappedPoints.length >= 3) {
    ctx.beginPath()
    ctx.moveTo(mappedPoints[0].x, mappedPoints[0].y)
    mappedPoints.slice(1).forEach((point) => ctx.lineTo(point.x, point.y))
    ctx.closePath()
    ctx.stroke()
  } else if (mappedPoints.length === 2) {
    ctx.beginPath()
    ctx.moveTo(mappedPoints[0].x, mappedPoints[0].y)
    ctx.lineTo(mappedPoints[1].x, mappedPoints[1].y)
    ctx.stroke()
  }

  ctx.strokeRect(
    Math.max(minX - padding, 0),
    Math.max(minY - padding, 0),
    Math.min(maxX - minX + padding * 2, width),
    Math.min(maxY - minY + padding * 2, height),
  )

  mappedPoints.forEach((point) => {
    ctx.beginPath()
    ctx.arc(point.x, point.y, 5, 0, Math.PI * 2)
    ctx.fill()
  })
  ctx.restore()
}

const drawIdleTracker = (canvasElement: HTMLCanvasElement | null) => {
  if (!canvasElement) {
    return
  }

  const preparedCanvas = prepareCanvas(canvasElement)

  if (preparedCanvas) {
    preparedCanvas.ctx.clearRect(0, 0, preparedCanvas.width, preparedCanvas.height)
  }
}

const computeDecodeCanvasSize = (
  videoWidth: number,
  videoHeight: number,
): { width: number; height: number } => {
  const longestEdge = Math.max(videoWidth, videoHeight, 1)

  if (longestEdge <= DECODE_LONG_EDGE_PX) {
    return { width: Math.max(videoWidth, 1), height: Math.max(videoHeight, 1) }
  }

  const scale = DECODE_LONG_EDGE_PX / longestEdge

  return {
    width: Math.max(Math.round(videoWidth * scale), 1),
    height: Math.max(Math.round(videoHeight * scale), 1),
  }
}

export const ZxingBarcodeScanner = ({
  formats,
  constraints = DEFAULT_SCANNER_CONSTRAINTS,
  scanDelay = 500,
  mirror = false,
  onScan,
  onError,
}: ZxingBarcodeScannerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const onScanRef = useRef(onScan)
  const onErrorRef = useRef(onError)
  const trackerClearTimeoutRef = useRef<number | null>(null)
  const lastSoundAtRef = useRef(0)

  useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => {
    const videoElement = videoRef.current

    if (!videoElement) {
      return undefined
    }

    let isDisposed = false
    let stream: MediaStream | null = null
    let cancelScheduledRef: (() => void) | null = null

    const worker = new Worker(new URL('./barcode-decoder.worker.ts', import.meta.url), {
      type: 'module',
    })

    const startScanner = async () => {
      log('starting scanner with caller constraints =', constraints, 'formats =', formats)
      stream = await getCameraStream(constraints)

      if (isDisposed) {
        stopMediaStream(stream)
        return
      }

      const [trackForLogs] = stream.getVideoTracks()
      if (trackForLogs) {
        log('granted track label =', trackForLogs.label)
        log('granted track settings =', trackForLogs.getSettings?.())
        if (trackForLogs.getCapabilities) {
          log('granted track capabilities =', trackForLogs.getCapabilities())
        }
      }

      videoElement.srcObject = stream
      videoElement.muted = true
      videoElement.playsInline = true
      await videoElement.play()
      await waitForVideoReady(videoElement)
      log(
        'video ready: videoWidth =',
        videoElement.videoWidth,
        ' videoHeight =',
        videoElement.videoHeight,
      )
      drawIdleTracker(canvasRef.current)

      if (isDisposed) {
        return
      }

      const videoWithRvfc = videoElement as VideoElementWithRvfc
      let rvfcHandle: RvfcHandle | null = null
      let rafHandle: number | null = null
      let timeoutHandle: number | null = null
      let lastDecodedMediaTime = -1
      let lastScanAt = 0
      let pendingDecodeId: number | null = null
      let nextDecodeId = 0

      const cancelScheduled = () => {
        if (rvfcHandle !== null) {
          videoWithRvfc.cancelVideoFrameCallback?.(rvfcHandle)
          rvfcHandle = null
        }
        if (rafHandle !== null) {
          window.cancelAnimationFrame(rafHandle)
          rafHandle = null
        }
        if (timeoutHandle !== null) {
          window.clearTimeout(timeoutHandle)
          timeoutHandle = null
        }
      }

      cancelScheduledRef = cancelScheduled

      const scheduleNextDecode = () => {
        if (isDisposed) {
          return
        }

        if (document.hidden) {
          timeoutHandle = window.setTimeout(scheduleNextDecode, 250)
          return
        }

        if (typeof videoWithRvfc.requestVideoFrameCallback === 'function') {
          rvfcHandle = videoWithRvfc.requestVideoFrameCallback((_now, metadata) => {
            rvfcHandle = null
            const capturedMediaTime = metadata.mediaTime
            timeoutHandle = window.setTimeout(() => {
              timeoutHandle = null
              void runDecodeTick(capturedMediaTime)
            }, 0)
          })
          return
        }

        rafHandle = window.requestAnimationFrame(() => {
          rafHandle = null
          void runDecodeTick(performance.now() / 1000)
        })
      }

      let noResultStreak = 0
      let workerErrorCount = 0

      const handleWorkerResult = (response: DecodeResponse) => {
        if (response.id !== pendingDecodeId) {
          return
        }
        pendingDecodeId = null

        if (isDisposed) {
          return
        }

        if (response.error) {
          warn('worker decode error:', response.error)
          onErrorRef.current(new Error(response.error))
          scheduleNextDecode()
          return
        }
        workerErrorCount = 0

        if (!response.found) {
          noResultStreak += 1
          if (noResultStreak % DECODE_LOG_INTERVAL === 0) {
            log('still scanning, no decode in last', DECODE_LOG_INTERVAL, 'attempts')
          }
        }

        if (response.found) {
          const { found } = response
          noResultStreak = 0
          log('DECODED format =', found.format, ' raw =', found.rawValue)
          // Worker reports points in its own decode-canvas coordinates. Map them
          // back to native video coordinates so the existing tracker maths apply.
          const videoScaleX =
            found.decodeWidth > 0 ? videoElement.videoWidth / found.decodeWidth : 1
          const videoScaleY =
            found.decodeHeight > 0 ? videoElement.videoHeight / found.decodeHeight : 1
          const pointsInVideoCoords = found.points.map((point) => ({
            x: point.x * videoScaleX,
            y: point.y * videoScaleY,
          }))

          drawResultTracker(canvasRef.current, videoElement, pointsInVideoCoords)

          if (trackerClearTimeoutRef.current) {
            window.clearTimeout(trackerClearTimeoutRef.current)
          }
          trackerClearTimeoutRef.current = window.setTimeout(() => {
            drawIdleTracker(canvasRef.current)
          }, TRACKER_CLEAR_DELAY_MS)

          const soundNow = Date.now()
          if (soundNow - lastSoundAtRef.current > SUCCESS_SOUND_THROTTLE_MS) {
            lastSoundAtRef.current = soundNow
            playSuccessSound()
          }

          onScanRef.current([
            {
              rawValue: found.rawValue,
              // format is already a normalised string from the worker
              // (e.g. 'data_matrix', 'qr_code', 'upc_a')
              format: found.format,
            },
          ])
        }

        scheduleNextDecode()
      }

      worker.onmessage = (event: MessageEvent<DecodeResponse>) => {
        handleWorkerResult(event.data)
      }

      worker.onerror = (event) => {
        if (isDisposed) {
          return
        }
        warn('worker.onerror:', event.message, event)
        onErrorRef.current(new Error(`Barcode decoder worker error: ${event.message}`))
        pendingDecodeId = null
        // Exponential back-off on repeated errors to prevent a tight error loop.
        // Resets to 0 on the next successful decode (see handleWorkerResult).
        workerErrorCount += 1
        const backoffDelay = Math.min(100 * workerErrorCount, 2000)
        timeoutHandle = window.setTimeout(() => {
          timeoutHandle = null
          scheduleNextDecode()
        }, backoffDelay)
      }

      const runDecodeTick = async (mediaTime: number) => {
        if (isDisposed) {
          return
        }

        // Skip if the worker is still busy with the previous decode.
        if (pendingDecodeId !== null) {
          scheduleNextDecode()
          return
        }

        const now = performance.now()
        const frameUnchanged = mediaTime === lastDecodedMediaTime
        const throttled = now - lastScanAt < scanDelay

        if (frameUnchanged || throttled) {
          scheduleNextDecode()
          return
        }

        const target = computeDecodeCanvasSize(videoElement.videoWidth, videoElement.videoHeight)

        if (target.width <= 0 || target.height <= 0 || videoElement.videoWidth <= 0) {
          scheduleNextDecode()
          return
        }

        let bitmap: ImageBitmap
        try {
          bitmap = await createImageBitmap(
            videoElement,
            0,
            0,
            videoElement.videoWidth,
            videoElement.videoHeight,
            {
              resizeWidth: target.width,
              resizeHeight: target.height,
              resizeQuality: 'medium',
            },
          )
        } catch (error) {
          if (!isDisposed) {
            onErrorRef.current(error)
          }
          scheduleNextDecode()
          return
        }

        if (isDisposed) {
          bitmap.close()
          return
        }

        lastDecodedMediaTime = mediaTime
        lastScanAt = now

        const decodeId = ++nextDecodeId
        pendingDecodeId = decodeId

        const request: DecodeRequest = {
          type: 'decode',
          id: decodeId,
          imageBitmap: bitmap,
          cropScales: Array.from(DECODE_CROP_SCALES),
          invertAtCropScale: DECODE_INVERT_AT_CROP_SCALE,
          formats: formats ? Array.from(formats) : null,
        }
        worker.postMessage(request, [bitmap])
      }

      scheduleNextDecode()
    }

    startScanner().catch((error) => {
      if (!isDisposed) {
        onErrorRef.current(error)
      }
    })

    return () => {
      isDisposed = true
      cancelScheduledRef?.()
      cancelScheduledRef = null
      worker.terminate()
      if (trackerClearTimeoutRef.current) {
        window.clearTimeout(trackerClearTimeoutRef.current)
      }
      stopMediaStream(stream)
      videoElement.pause()
      videoElement.srcObject = null
    }
  }, [constraints, formats, scanDelay])

  const mirrorTransform = mirror ? 'scaleX(-1)' : undefined

  return (
    <>
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        aria-label='Barcode scanner camera'
        style={mirrorTransform ? { transform: mirrorTransform } : undefined}
      />
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          transform: mirrorTransform,
        }}
      />
    </>
  )
}
